import { useState, useEffect } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';

import {
    UpdateVideoDocument,
    VideoEditDocument,
    DeleteVideoDocument,
    IContentTypeValue,
} from 'graphql/types';
import {
    IPageDrawerProps,
    PageDrawer,
} from 'common/components/PageDrawer/PageDrawer';
import { Loader } from 'common/components/Loader';
import { useApolloError } from 'common/hooks/useApolloError';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { PageTitle } from 'common/components/PageTitle';
import { VideoForm, TVideoFormValues } from 'video/components/forms/VideoForm';
import { getVideoThumbnailId } from 'video/utils/thumbnail';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import { AuthorButton } from 'user/components/AuthorButton';
import { getExtraCategoryInitialFormValues } from 'organisation/utils/extraCategory';
import { useExtraCategoryValues } from 'common/hooks/useExtraCategoryValues';

interface IProps extends IPageDrawerProps {
    id?: string;
    onClose?(): void;
}

export const VideoUpdateDrawer = ({ id, open, onClose, ...other }: IProps) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();

    const [showDeleteAlert, setShowDeleteAlert] = useState<boolean>(false);

    const [getVideo, { loading: loadingVideo, data: videoData, called }] =
        useLazyQuery(VideoEditDocument, {
            fetchPolicy: 'cache-and-network',
            notifyOnNetworkStatusChange: true,
        });

    const video = videoData?.video;
    const isVideoLoading = !called || loadingVideo;
    const [updateVideo, { loading: loadingUpdate }] =
        useMutation(UpdateVideoDocument);
    const [deleteVideo, { loading: loadingDelete }] =
        useMutation(DeleteVideoDocument);
    const {
        extraCategories,
        loading: extraCategoriesLoading,
        handleUpdateExtraCategoryValues,
    } = useExtraCategoryValues({
        contentType: IContentTypeValue.Video,
        modelId: video?.id || '',
        skipQuery: !video?.id,
        typeNames: ['Video'],
    });

    useEffect(() => {
        if (!open || !id) return;

        getVideo({ variables: { id } });
    }, [id, open, getVideo]);

    if (open && (isVideoLoading || !video || extraCategoriesLoading)) {
        return (
            <PageDrawer open={open}>
                <Loader />
            </PageDrawer>
        );
    }

    if (!video) {
        return null;
    }

    const { title, thumbnail, pluvoEmbedUrl, extraCategoryValues } = video;

    const initialValues: TVideoFormValues = {
        title: title || '',
        videoUrl: pluvoEmbedUrl || '',
        thumbnail,
        extraCategories: getExtraCategoryInitialFormValues(
            extraCategories,
            extraCategoryValues
        ),
    };

    const handleClose = () => {
        onClose?.();
    };

    const handleSubmit = async (values: TVideoFormValues) => {
        if (!id) return;

        const {
            videoUrl,
            thumbnail,
            extraCategories: extraCategoryUpdateValues,
            ...otherValues
        } = values;
        const { thumbnail: currentThumbnail } = video;

        const newValues = {
            ...otherValues,
            thumbnailId: getVideoThumbnailId(id, currentThumbnail, thumbnail),
        };

        try {
            await updateVideo({
                variables: {
                    id,
                    video: newValues,
                },
                update: (cache) => {
                    cache.evict({ fieldName: 'videos' });
                    cache.gc();
                },
            });

            await handleUpdateExtraCategoryValues(
                extraCategoryValues,
                extraCategoryUpdateValues
            );
        } catch (error) {
            showApolloError(error);

            return;
        }

        displaySnackbar(translate('videoActionSuccess.update'), {
            variant: 'success',
        });

        // Close drawer
        handleClose();

        return;
    };

    const handleDelete = async () => {
        if (!id) return;

        try {
            await deleteVideo({
                variables: { id },
                update: (cache) => {
                    // Remove videos list from cache so it will be refetched
                    cache.evict({ fieldName: 'videos' });

                    // Remove the specific video from cache
                    cache.evict({ id: `Video:${id}` });

                    cache.gc();
                },
            });
        } catch (error) {
            setShowDeleteAlert(false);
            showApolloError(error);

            return;
        }

        displaySnackbar(translate('moduleActionSuccess.video.delete'), {
            variant: 'success',
        });

        setShowDeleteAlert(false);

        // Close drawer
        handleClose();

        return;
    };

    const isMutating = loadingUpdate || loadingDelete || extraCategoriesLoading;

    const drawerActions = !!id && (
        <AuthorButton contentType={IContentTypeValue.Video} objectId={id} />
    );

    return (
        <PageDrawer
            actions={drawerActions}
            disableClose={isMutating}
            open={open}
            onClose={handleClose}
            {...other}
        >
            <PageTitle>{translate('videoForm.pageTitle')}</PageTitle>

            <VideoForm
                initialValues={initialValues}
                key={id}
                video={video}
                onClose={handleClose}
                onDelete={() => setShowDeleteAlert(true)}
                onSubmit={handleSubmit}
            />

            <ConfirmDialog
                confirmText={translate('delete')}
                loading={loadingDelete}
                open={showDeleteAlert}
                title={translate('deleteVideoMessage.title')}
                onCancel={() => {
                    setShowDeleteAlert(false);
                }}
                onClose={() => setShowDeleteAlert(false)}
                onConfirm={handleDelete}
            >
                {translate('deleteVideoMessage.text')}
            </ConfirmDialog>
        </PageDrawer>
    );
};
